import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import {
  AUTH_STORAGE_KEYS,
  MAX_TIMEOUT_MS,
  SESSION_WARNING_BEFORE_MS,
  setUnauthorizedHandler
} from '../api/http';

const formatRemainingTime = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const scheduleLongTimeout = (callback: () => void, delayMs: number): (() => void) => {
  let timeoutId: number | undefined;
  let cancelled = false;

  const schedule = (remainingMs: number) => {
    timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      const nextRemaining = remainingMs - MAX_TIMEOUT_MS;
      if (nextRemaining > 0) {
        schedule(nextRemaining);
        return;
      }
      callback();
    }, Math.min(remainingMs, MAX_TIMEOUT_MS));
  };

  schedule(Math.max(0, delayMs));
  return () => {
    cancelled = true;
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  };
};

export default function SessionProvider() {
  const { isAuthenticated, tokenExpiresAt, logout, checkAuthStatus } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const { t } = useTranslation();

  locationRef.current = location;

  const logoutAndGoLogin = useCallback(() => {
    const current = locationRef.current;
    setWarningOpen(false);
    logout();
    navigate('/login', {
      replace: true,
      state: { from: `${current.pathname}${current.search}${current.hash}` }
    });
  }, [logout, navigate]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logoutAndGoLogin();
    });

    return () => setUnauthorizedHandler(null);
  }, [logoutAndGoLogin]);

  useEffect(() => {
    if (!isAuthenticated || !tokenExpiresAt) {
      setWarningOpen(false);
      return;
    }

    const msUntilExpiry = tokenExpiresAt - Date.now();
    if (msUntilExpiry <= 0) {
      logoutAndGoLogin();
      return;
    }

    const cleanupFns: Array<() => void> = [];
    const msUntilWarning = msUntilExpiry - SESSION_WARNING_BEFORE_MS;

    if (msUntilWarning <= 0) {
      setWarningOpen(true);
    } else {
      cleanupFns.push(scheduleLongTimeout(() => setWarningOpen(true), msUntilWarning));
    }

    cleanupFns.push(scheduleLongTimeout(logoutAndGoLogin, msUntilExpiry));
    return () => cleanupFns.forEach((fn) => fn());
  }, [isAuthenticated, logoutAndGoLogin, tokenExpiresAt]);

  useEffect(() => {
    if (!warningOpen || !tokenExpiresAt) return;

    const updateRemaining = () => {
      const next = tokenExpiresAt - Date.now();
      setRemainingMs(Math.max(0, next));
      if (next <= 0) logoutAndGoLogin();
    };

    updateRemaining();
    const intervalId = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(intervalId);
  }, [logoutAndGoLogin, tokenExpiresAt, warningOpen]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkAuthStatus();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEYS.token || event.key === AUTH_STORAGE_KEYS.tokenExpiresAt) {
        checkAuthStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [checkAuthStatus]);

  return (
    <>
      <Outlet />
      <Dialog open={warningOpen} onClose={() => setWarningOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('auth.session.warningTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {t('auth.session.warningMessage', { time: formatRemainingTime(remainingMs) })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={logoutAndGoLogin}>
            {t('auth.session.logoutNow')}
          </Button>
          <Button variant="contained" onClick={() => setWarningOpen(false)}>
            {t('actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
